<template>
  <div class="ci-ident" :style="cssVars">

    <!-- ══ Modo: pix-livre ══════════════════════════════════════════ -->
    <template v-if="isPixLivre">

      <!-- Seletor de perfil (quando há múltiplos salvos) -->
      <template v-if="savedCustomers.length > 0 && !showNewForm">
        <p class="ci-ident__eyebrow">Contribuição livre</p>
        <h3 class="ci-ident__title">Quem vai contribuir?</h3>

        <div class="ci-ident__profiles">
          <button
            v-for="c in savedCustomers"
            :key="c.cpf_cnpj"
            class="ci-ident__profile-btn"
            :disabled="loading"
            @click="selectCustomer(c)"
          >
            <div class="ci-ident__profile-avatar">{{ c.name?.charAt(0)?.toUpperCase() }}</div>
            <div class="ci-ident__profile-info">
              <span class="ci-ident__profile-name">{{ c.name?.split(' ')[0] }}</span>
              <span class="ci-ident__profile-doc">{{ maskDoc(c.cpf_cnpj) }}</span>
            </div>
            <i class="fas fa-chevron-right ci-ident__profile-arrow"></i>
          </button>
        </div>

        <button class="ci-ident__new-link" @click="showNewForm = true">
          <i class="fas fa-user-plus me-1"></i> Usar outros dados nessa contribuição
        </button>
      </template>

      <!-- Formulário -->
      <template v-else>
      <p class="ci-ident__eyebrow">Contribuição livre</p>
      <h3 class="ci-ident__title">Quanto quer contribuir?</h3>
      <p class="ci-ident__sub">Pague via Pix — contribuições a partir de R$ 50,00 💕</p>

      <!-- Valor (full width) -->
      <div class="ci-ident__row">
        <div class="ci-ident__field ci-ident__field--full">
          <label>Valor <span class="ci-ident__req">*</span></label>
          <input
            v-model="form.valueRaw"
            type="text"
            inputmode="numeric"
            placeholder="R$ 0,00"
            class="ci-ident__currency"
            :class="{ 'is-invalid': touched.value && errors.value }"
            @input="maskCurrency"
            @blur="touched.value = true"
            :disabled="loading"
          />
          <span v-if="touched.value && errors.value" class="ci-ident__error">{{ errors.value }}</span>
        </div>
      </div>

      <!-- CPF (full width) -->
      <div class="ci-ident__row">
        <div class="ci-ident__field ci-ident__field--full">
          <label>CPF <span class="ci-ident__req">*</span></label>
          <input
            v-model="form.cpf_cnpj"
            type="text"
            inputmode="numeric"
            placeholder="000.000.000-00"
            :class="{ 'is-invalid': touched.cpf_cnpj && errors.cpf_cnpj }"
            @input="maskCpfCnpj"
            @blur="touched.cpf_cnpj = true"
            :disabled="loading"
          />
          <span v-if="touched.cpf_cnpj && errors.cpf_cnpj" class="ci-ident__error">{{ errors.cpf_cnpj }}</span>
        </div>
      </div>

      <!-- Nome + E-mail (opcional) -->
      <div class="ci-ident__row">
        <div class="ci-ident__field">
          <label>Nome <span class="ci-ident__optional">(opcional)</span></label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Seu nome"
            :disabled="loading"
          />
        </div>
        <div class="ci-ident__field">
          <label>E-mail <span class="ci-ident__optional">(opcional)</span></label>
          <input
            v-model="form.email"
            type="email"
            placeholder="seu@email.com"
            :disabled="loading"
          />
        </div>
      </div>

      <button class="ci-ident__submit" :disabled="loading" @click="submitPixLivre">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="fas fa-qrcode me-2"></i>
        Gerar QR Code Pix
      </button>

      <button v-if="savedCustomers.length > 0" class="ci-ident__new-link" @click="showNewForm = false">
        <i class="fas fa-chevron-left me-1"></i> Voltar para perfis salvos
      </button>

      </template>
    </template>

    <!-- ══ Modos: pix | card ════════════════════════════════════════ -->
    <template v-else>

      <!-- Perfis salvos -->
      <template v-if="savedCustomers.length > 0 && !showNewForm">
        <p class="ci-ident__eyebrow">Quem vai presentear?</p>
        <h3 class="ci-ident__title">Selecione seu perfil</h3>

        <div class="ci-ident__profiles">
          <button
            v-for="c in savedCustomers"
            :key="c.cpf_cnpj"
            class="ci-ident__profile-btn"
            :disabled="loading"
            @click="selectCustomer(c)"
          >
            <div class="ci-ident__profile-avatar">{{ c.name?.charAt(0)?.toUpperCase() }}</div>
            <div class="ci-ident__profile-info">
              <span class="ci-ident__profile-name">{{ c.name?.split(' ')[0] }}</span>
              <span class="ci-ident__profile-doc">{{ maskDoc(c.cpf_cnpj) }}</span>
            </div>
            <span v-if="method === 'card' && !isComplete(c)" class="ci-ident__profile-badge">
              <i class="fas fa-map-marker-alt me-1"></i>Completar
            </span>
            <span v-if="loading && selectedCpf === c.cpf_cnpj" class="spinner-border spinner-border-sm flex-shrink-0"></span>
            <i v-else class="fas fa-chevron-right ci-ident__profile-arrow"></i>
          </button>
        </div>

        <button class="ci-ident__new-link" @click="showNewForm = true">
          <i class="fas fa-user-plus me-1"></i> Usar outros dados nessa compra
        </button>
      </template>

      <!-- Formulário -->
      <template v-else>
        <p class="ci-ident__eyebrow">Identificação</p>
        <h3 class="ci-ident__title">Seus dados</h3>
        <p class="ci-ident__sub">Precisamos identificar o presenteador 🎁</p>

        <!-- CPF + Nome -->
        <div class="ci-ident__row">
          <div class="ci-ident__field">
            <label>CPF / CNPJ <span class="ci-ident__req">*</span></label>
            <input
              v-model="form.cpf_cnpj"
              type="text"
              inputmode="numeric"
              placeholder="000.000.000-00"
              :class="{ 'is-invalid': touched.cpf_cnpj && errors.cpf_cnpj }"
              @input="maskCpfCnpj"
              @blur="touched.cpf_cnpj = true"
              :disabled="loading"
            />
            <span v-if="touched.cpf_cnpj && errors.cpf_cnpj" class="ci-ident__error">{{ errors.cpf_cnpj }}</span>
          </div>
          <div class="ci-ident__field">
            <label>Nome completo <span class="ci-ident__req">*</span></label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Seu nome"
              :class="{ 'is-invalid': touched.name && errors.name }"
              @blur="touched.name = true"
              :disabled="loading"
            />
            <span v-if="touched.name && errors.name" class="ci-ident__error">{{ errors.name }}</span>
          </div>
        </div>

        <!-- Email + Telefone -->
        <div class="ci-ident__row">
          <div class="ci-ident__field">
            <label>E-mail <span class="ci-ident__req">*</span></label>
            <input
              v-model="form.email"
              type="email"
              placeholder="seu@email.com"
              :class="{ 'is-invalid': touched.email && errors.email }"
              @blur="touched.email = true"
              :disabled="loading"
            />
            <span v-if="touched.email && errors.email" class="ci-ident__error">{{ errors.email }}</span>
          </div>
          <div class="ci-ident__field">
            <label>Telefone <span class="ci-ident__req">*</span></label>
            <input
              v-model="form.phone"
              type="text"
              inputmode="numeric"
              placeholder="(00) 00000-0000"
              :class="{ 'is-invalid': touched.phone && errors.phone }"
              @input="maskPhone"
              @blur="touched.phone = true"
              :disabled="loading"
            />
            <span v-if="touched.phone && errors.phone" class="ci-ident__error">{{ errors.phone }}</span>
          </div>
        </div>

        <!-- CEP + Número — somente cartão -->
        <template v-if="method === 'card'">
          <div class="ci-ident__row">
            <div class="ci-ident__field">
              <label>CEP <span class="ci-ident__req">*</span></label>
              <div class="ci-ident__field-rel">
                <input
                  v-model="form.postal_code"
                  type="text"
                  inputmode="numeric"
                  placeholder="00000-000"
                  :class="{ 'is-invalid': touched.postal_code && errors.postal_code }"
                  @input="maskCep"
                  @blur="touched.postal_code = true"
                  :disabled="loading"
                />
                <span v-if="loadingCep" class="ci-ident__cep-spinner">
                  <span class="spinner-border spinner-border-sm"></span>
                </span>
              </div>
              <span v-if="touched.postal_code && errors.postal_code" class="ci-ident__error">{{ errors.postal_code }}</span>
            </div>
            <div class="ci-ident__field">
              <label>Número <span class="ci-ident__req">*</span></label>
              <input
                v-model="form.address_number"
                type="text"
                placeholder="Ex: 123"
                :class="{ 'is-invalid': touched.address_number && errors.address_number }"
                @blur="touched.address_number = true"
                :disabled="loading"
              />
              <span v-if="touched.address_number && errors.address_number" class="ci-ident__error">{{ errors.address_number }}</span>
            </div>
          </div>
        </template>

        <!-- Voltar para perfis -->
        <button v-if="savedCustomers.length > 0" class="ci-ident__new-link" @click="showNewForm = false">
          <i class="fas fa-chevron-left me-1"></i> Voltar para perfis salvos
        </button>

        <!-- Botão avançar -->
        <button class="ci-ident__submit" :disabled="loading" @click="submit">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="fas fa-arrow-right me-2"></i>
          {{ method === 'pix' ? 'Gerar QR Code Pix' : 'Avançar' }}
        </button>
      </template>

    </template>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { createGuest } from '@/services/guestService';
import { useCustomerStore } from '@/stores/customerStore';
import { toast } from 'vue3-toastify';

const props = defineProps({
  method:       { type: String, required: true }, // 'pix' | 'card' | 'pix-livre'
  eventId:      { type: String, required: true },
  primaryColor: { type: String, default: '#ff50a3' },
});

const cssVars = computed(() => ({ '--ci-primary': props.primaryColor }));

const emit = defineEmits(['identified']);

const MIN_PIX_LIVRE_VALUE = 50;

const isPixLivre = computed(() => props.method === 'pix-livre');

const customerStore = useCustomerStore();
const savedCustomers = computed(() => customerStore.customers || []);

const showNewForm = ref(savedCustomers.value.length === 0);
const loading = ref(false);
const loadingCep = ref(false);
const selectedCpf = ref('');

let cepAbortCtrl = null;
let cepTimer = null;

const form = reactive({
  cpf_cnpj: '',
  name: '',
  email: '',
  phone: '',
  postal_code: '',
  address_number: '',
  valueRaw: '',
});

const touched = reactive({
  cpf_cnpj: false, name: false, email: false,
  phone: false, postal_code: false, address_number: false,
  value: false,
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cpfDigits = computed(() => form.cpf_cnpj.replace(/\D/g, ''));

const parsedValue = computed(() => {
  if (!form.valueRaw) return 0;
  return parseFloat(form.valueRaw.replace(/\./g, '').replace(',', '.')) || 0;
});

// ── Pré-preenche pix-livre com dados do cliente cacheado ──────────
onMounted(() => {
  if (!isPixLivre.value) return;
  const prefill = customerStore.activeCustomer || savedCustomers.value[0] || null;
  if (!prefill) return;
  if (prefill.cpf_cnpj) {
    form.cpf_cnpj = applyDocMask(prefill.cpf_cnpj);
    touched.cpf_cnpj = true;
  }
  if (prefill.name)  form.name  = prefill.name;
  if (prefill.email) form.email = prefill.email;
});

const errors = computed(() => ({
  cpf_cnpj: (cpfDigits.value.length !== 11 && cpfDigits.value.length !== 14)
    ? 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido' : null,
  name: !isPixLivre.value && form.name.trim().length < 3 ? 'Informe seu nome completo' : null,
  email: !isPixLivre.value && !emailRegex.test(form.email.trim()) ? 'Informe um e-mail válido' : null,
  phone: !isPixLivre.value && form.phone.replace(/\D/g, '').length < 10 ? 'Informe um telefone válido' : null,
  postal_code: props.method === 'card' && form.postal_code.replace(/\D/g, '').length !== 8
    ? 'Informe um CEP válido' : null,
  address_number: props.method === 'card' && !form.address_number.trim()
    ? 'Informe o número' : null,
  value: isPixLivre.value && parsedValue.value < MIN_PIX_LIVRE_VALUE
    ? 'A contribuição mínima é de R$ 50,00' : null,
}));

// ── Masks ─────────────────────────────────────────────────────────
function applyDocMask(raw) {
  let v = raw.replace(/\D/g, '');
  if (v.length <= 11) {
    v = v.replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    v = v.slice(0, 14)
         .replace(/(\d{2})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1/$2')
         .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
  return v;
}

function maskCpfCnpj(e) {
  form.cpf_cnpj = applyDocMask(e.target.value);
}

function maskPhone(e) {
  let v = e.target.value.replace(/\D/g, '').slice(0, 11);
  if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length > 10) v = v.slice(0,10) + '-' + v.slice(10);
  form.phone = v;
}

function maskCep(e) {
  let v = e.target.value.replace(/\D/g, '').slice(0, 8);
  if (v.length > 5) v = v.slice(0,5) + '-' + v.slice(5);
  form.postal_code = v;
  const clean = v.replace(/\D/g, '');
  if (clean.length === 8) scheduleCepLookup(clean);
}

function maskCurrency(e) {
  let v = e.target.value.replace(/\D/g, '');
  if (!v) { form.valueRaw = ''; return; }
  const num = parseInt(v, 10) / 100;
  form.valueRaw = num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function scheduleCepLookup(cep) {
  if (cepTimer) clearTimeout(cepTimer);
  cepTimer = setTimeout(() => fetchCep(cep), 300);
}

async function fetchCep(cep) {
  if (cepAbortCtrl) cepAbortCtrl.abort();
  cepAbortCtrl = new AbortController();
  loadingCep.value = true;
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cep/v1/${cep}`, { signal: cepAbortCtrl.signal });
    if (!res.ok) throw new Error('CEP não encontrado');
  } catch (err) {
    if (err.name === 'AbortError') return;
    toast.warning('CEP não encontrado. Verifique e continue.', { autoClose: 3000 });
  } finally {
    loadingCep.value = false;
    cepAbortCtrl = null;
  }
}

// ── Helpers ───────────────────────────────────────────────────────
function maskDoc(cpf) {
  const d = (cpf || '').replace(/\D/g, '');
  if (d.length === 11) return `***.***.${d.slice(6,9)}-**`;
  if (d.length === 14) return `**.***.***/***${d.slice(12)}-**`;
  return cpf;
}

function isComplete(c) {
  return !!(c.postal_code && c.address_number);
}

// ── pix-livre submit ──────────────────────────────────────────────
function submitPixLivre() {
  touched.value = true;
  touched.cpf_cnpj = true;

  if (errors.value.value) {
    toast.warning(errors.value.value, { autoClose: 3000 });
    return;
  }
  if (errors.value.cpf_cnpj) {
    toast.warning(errors.value.cpf_cnpj, { autoClose: 3000 });
    return;
  }

  emit('identified', {
    name:      form.name.trim() || null,
    email:     form.email.trim() || null,
    cpf_cnpj:  cpfDigits.value,
    value:     parsedValue.value,
  });
}

// ── Select saved customer ─────────────────────────────────────────
async function selectCustomer(c) {
  // Pix-livre: pré-preenche e vai para o formulário de valor
  if (isPixLivre.value) {
    form.cpf_cnpj = applyDocMask(c.cpf_cnpj);
    touched.cpf_cnpj = true;
    form.name  = c.name  || '';
    form.email = c.email || '';
    showNewForm.value = true;
    return;
  }
  if (props.method === 'card' && !isComplete(c)) {
    form.cpf_cnpj = applyDocMask(c.cpf_cnpj);
    form.name = c.name;
    form.email = c.email || '';
    form.phone = c.phone || '';
    showNewForm.value = true;
    return;
  }
  selectedCpf.value = c.cpf_cnpj;
  customerStore.setActiveCustomer(c);
  emit('identified', c);
}

// ── Submit new form (pix | card) ──────────────────────────────────
async function submit() {
  Object.keys(touched).forEach(k => touched[k] = true);

  const fields = ['cpf_cnpj', 'name', 'email', 'phone',
    ...(props.method === 'card' ? ['postal_code', 'address_number'] : [])];

  const firstErr = fields.find(f => errors.value[f]);
  if (firstErr) {
    toast.warning(errors.value[firstErr], { autoClose: 3000 });
    return;
  }

  loading.value = true;
  try {
    const result = await createGuest({
      name:           form.name.trim(),
      cpf_cnpj:       cpfDigits.value,
      email:          form.email.trim(),
      phone:          form.phone.replace(/\D/g, ''),
      postal_code:    form.postal_code.replace(/\D/g, ''),
      address_number: form.address_number.trim(),
    });

    const customer = {
      id: result.id,
      provider_customer_id: result.provider_customer_id || result.asaas_customer_id,
      name: form.name.trim(),
      cpf_cnpj: cpfDigits.value,
      email: form.email.trim(),
      phone: form.phone.replace(/\D/g, ''),
      postal_code: form.postal_code.replace(/\D/g, ''),
      address_number: form.address_number.trim(),
    };

    customerStore.addOrUpdateCustomer(customer);
    emit('identified', customer);
  } catch (err) {
    const msg = err?.response?.data?.message || 'Erro ao salvar seus dados. Tente novamente.';
    toast.error(msg, { autoClose: 5000 });
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.ci-ident {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ci-ident__eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ci-primary, #ff50a3);
  margin: 0 0 0.25rem;
}

.ci-ident__title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
  margin: 0 0 0.2rem;
}

.ci-ident__sub {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.5);
  margin: 0 0 1.25rem;
}

/* ── Saved profiles ── */
.ci-ident__profiles {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.ci-ident__profile-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 0.75rem;
  padding: 0.65rem 0.85rem;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;
  text-align: left;
  width: 100%;
}
.ci-ident__profile-btn:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.2);
}

.ci-ident__profile-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ci-ident__profile-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.ci-ident__profile-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ci-ident__profile-doc {
  font-size: 0.72rem;
  color: rgba(255,255,255,0.4);
}

.ci-ident__profile-badge {
  font-size: 0.65rem;
  font-weight: 600;
  color: #facc15;
  border: 1px solid rgba(250,204,21,0.3);
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  white-space: nowrap;
}

.ci-ident__profile-arrow {
  color: rgba(255,255,255,0.25);
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* ── Form ── */
.ci-ident__row {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

.ci-ident__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
}

.ci-ident__field label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
}

.ci-ident__req {
  color: var(--ci-primary, #ff50a3);
}

.ci-ident__optional {
  font-size: 0.65rem;
  font-weight: 400;
  color: rgba(255,255,255,0.3);
  text-transform: none;
  letter-spacing: 0;
}

/* ── Value field ── */
.ci-ident__field--full {
  flex: 0 0 100%;
}

.ci-ident__currency {
  font-size: 1.5rem !important;
  font-weight: 800 !important;
  text-align: center;
  letter-spacing: -0.02em;
  color: #fff !important;
  padding: 0.75rem 1rem !important;
}

.ci-ident__field input {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 0.6rem;
  padding: 0.55rem 0.75rem;
  font-size: 0.88rem;
  color: #fff;
  outline: none;
  transition: border-color 0.18s, background 0.18s;
  width: 100%;
}
.ci-ident__field input::placeholder { color: rgba(255,255,255,0.25); }
.ci-ident__field input:focus {
  border-color: var(--ci-primary, #ff50a3);
  background: rgba(255,255,255,0.1);
}
.ci-ident__field input.is-invalid {
  border-color: #f87171;
}

.ci-ident__error {
  font-size: 0.7rem;
  color: #f87171;
}

.ci-ident__field-rel {
  position: relative;
}
.ci-ident__cep-spinner {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255,255,255,0.4);
}

/* ── New link ── */
.ci-ident__new-link {
  background: none;
  border: none;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  padding: 0.5rem 0;
  text-align: center;
  transition: color 0.18s;
  width: 100%;
}
.ci-ident__new-link:hover { color: rgba(255,255,255,0.75); }

/* ── Submit ── */
.ci-ident__submit {
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.85rem 1rem;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.18s, transform 0.15s;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--ci-primary, #ff50a3) 40%, transparent);
}
.ci-ident__submit:active { transform: scale(0.97); }
.ci-ident__submit:disabled { opacity: 0.6; cursor: default; }
</style>
